import React, { useState, useEffect } from "react";
import Navbar from "../NavBar";
import NewSidebar from "../newSideBar";
import SettingSideBar from "./SettingSideBar";
import axios from "axios";
import { Clock, AlertCircle, CheckCircle } from "react-feather";

const OperatingHour = () => {
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [errors, setErrors] = useState({});
  const localURL = localStorage.getItem("shopURL");

  const headers = {
    "Content-Type": "application/json",
    "x-shop-url": localURL,
  };

  // Fetch existing working hours from DB
  useEffect(() => {
    const fetchOperatingHours = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/operating-hours`,
          { headers }
        );
        if (response.data) {
          setFromTime(response.data.fromTime || "");
          setToTime(response.data.toTime || "");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showNotification("Error fetching operating hours", "error");
      }
    };

    fetchOperatingHours();
    initializeBootstrapTooltips();
  }, []);

  const initializeBootstrapTooltips = () => {
    const bootstrap = require("bootstrap");
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]'
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new bootstrap.Tooltip(tooltipTriggerEl);
    });
  };

  const validateTimes = () => {
    const newErrors = {};
    
    if (!fromTime) {
      newErrors.fromTime = "Start time is required";
    }
    
    if (!toTime) {
      newErrors.toTime = "End time is required";
    }
    
    if (fromTime && toTime && fromTime >= toTime) {
      newErrors.timeRange = "End time must be after start time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSave = async () => {
    if (!validateTimes()) return;

    setIsSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/operating-hours`,
        { fromTime, toTime },
        { headers }
      );
      showNotification("Operating hours saved successfully");
    } catch (error) {
      console.error("Error saving data:", error);
      showNotification("Error saving operating hours", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column w-100">
      <Navbar />
      <div className="d-flex">
        <NewSidebar />
        <div style={{ display: "flex", flex: 1 }}>
          <SettingSideBar />
          <div className="main-setting-container vh-100 p-4">
            <div className="d-flex align-items-center mb-4">
              <Clock size={24} className="me-2 text-primary" />
              <h3 className="chat-title mb-0">
                Operating Hours
                <span
                  className="ms-2"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Set the admin's available hours so users can see when they are online or offline."
                  style={{ fontSize: "16px" }}
                >
                  <i className="bi bi-info-circle"></i>
                </span>
              </h3>
            </div>

            <div className="setting-card shadow-sm p-4 mb-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Start Time</label>
                <input
                  type="time"
                  className={`form-control ${errors.fromTime ? "is-invalid" : ""}`}
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                />
                {errors.fromTime && (
                  <div className="invalid-feedback d-flex align-items-center">
                    <AlertCircle size={16} className="me-1" />
                    {errors.fromTime}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">End Time</label>
                <input
                  type="time"
                  className={`form-control ${errors.toTime ? "is-invalid" : ""}`}
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                />
                {errors.toTime && (
                  <div className="invalid-feedback d-flex align-items-center">
                    <AlertCircle size={16} className="me-1" />
                    {errors.toTime}
                  </div>
                )}
              </div>

              {errors.timeRange && (
                <div className="alert alert-warning d-flex align-items-center">
                  <AlertCircle size={16} className="me-2" />
                  {errors.timeRange}
                </div>
              )}

              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-primary px-4"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>

            {/* Current Hours Preview */}
            {fromTime && toTime && !errors.timeRange && (
              <div className="setting-card shadow-sm p-4">
                <h5 className="mb-3">Current Operating Hours</h5>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <span className="badge bg-primary p-2">
                      {formatTimeForDisplay(fromTime)}
                    </span>
                    <span className="mx-2">to</span>
                    <span className="badge bg-primary p-2">
                      {formatTimeForDisplay(toTime)}
                    </span>
                  </div>
                  <span className="text-success d-flex align-items-center">
                    <CheckCircle size={16} className="me-1" />
                    Valid time range
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`notification position-fixed bottom-0 end-0 m-4 p-3 rounded shadow ${
            notification.type === "error" ? "bg-danger" : "bg-success"
          } text-white`}
          style={{ zIndex: 1100 }}
        >
          <div className="d-flex align-items-center">
            {notification.type === "error" ? (
              <AlertCircle size={20} className="me-2" />
            ) : (
              <CheckCircle size={20} className="me-2" />
            )}
            {notification.message}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format time for display
const formatTimeForDisplay = (timeString) => {
  if (!timeString) return "";
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export default OperatingHour;